import { AuthTokenHeader, FetchClient } from '@vp/fetch-client';
import { GalleryContentQueryClient, ScenesPurpose } from '@vp/http-clients';
import { ILogger } from '@vp/pdp-ubik-logging';

import { configuration } from '../../../infrastructure/clientConfigurationProvider';
import { completeCombination } from '../../../infrastructure/managers/ranchManager';
import { ICurrentDocument, HeroCarouselMedias } from '../../../types/interfaces';

import { Combination } from './Combination';
import { CombinationMedia } from './CombinationMedia';
import { SlideArray } from './SlideArray';
import { Video } from './Video';
export class CarouselData {
  private constructor(
    private _commonImages: SlideArray,
    private _combinationMedias: CombinationMedia[],
    private _documentCombinationMedias: CombinationMedia[] = [],
    private _brandkitPreviewMedias: CombinationMedia[] = []
  ) {}

  get containsVideos(): boolean {
    return (
      this._commonImages.containsVideos || this._combinationMedias.some((cm) => cm.containsVideos)
    );
  }

  getSlidesByCombination(selectedCombination: Combination): SlideArray {
    const referentialMedias = this.getCombinationMediasSlidesForCombination(selectedCombination);
    const brandkitCombinationMedias = CarouselData.findCompatibleCombinationMedia(
      this._brandkitPreviewMedias,
      selectedCombination
    );
    return SlideArray.fromSlides(
      brandkitCombinationMedias?.medias.all || [],
      this._commonImages.all,
      referentialMedias.all
    );
  }

  getReferentialImageIndex(selectedCombination: Combination): number | null {
    const referentialMedias = this.getCombinationMediasSlidesForCombination(selectedCombination);
    if (referentialMedias.isEmpty) return null;
    return this._commonImages.all.length;
  }

  getDocumentImageIndex(selectedCombination: Combination): number | null {
    const documentSelectedCombinationMedias = CarouselData.findStrictCompatibleCombinationMedia(
      this._documentCombinationMedias,
      selectedCombination
    );

    if (!documentSelectedCombinationMedias) {
      return null;
    }

    return this._commonImages.all.length;
  }

  getBrandkitImageIndex(selectedCombination: Combination): number | null {
    const brandkitCombinationMedias = CarouselData.findCompatibleCombinationMedia(
      this._brandkitPreviewMedias,
      selectedCombination
    );

    if (!brandkitCombinationMedias) {
      return null;
    }

    return 0;
  }

  getFirstCombination(): Combination {
    if (this._combinationMedias.length === 0) {
      return Combination.empty();
    }

    return this._combinationMedias[0].combination;
  }

  getAllThumbnails(): string[] {
    const referentialImagesThumbnails: string[] = this._combinationMedias.flatMap(
      (combinationMedia) => combinationMedia.thumbnails
    );
    const documentImagesThumbnails: string[] = this._documentCombinationMedias.flatMap(
      (combinationMedia) => combinationMedia.thumbnails
    );
    return this._commonImages.thumbnails.concat(
      documentImagesThumbnails,
      referentialImagesThumbnails
    );
  }

  getFirstVisibleImages(): string[] {
    const commonImages = this._commonImages.galleryImagesUrls;

    const firstCombinationImages =
      this._combinationMedias.length > 0 ? this._combinationMedias[0].medias.galleryImagesUrls : [];

    return commonImages.concat(firstCombinationImages);
  }

  getFirstVisibleVideos(): Video[] {
    const commonVideos = this._commonImages.videos;

    const firstCombinationVideos =
      this._combinationMedias.length > 0 ? this._combinationMedias[0].medias.videos : [];

    return commonVideos.concat(firstCombinationVideos);
  }

  copy(): CarouselData {
    const combinationMedias = this._combinationMedias.map((combinationMedia) =>
      combinationMedia.copy()
    );
    const medias = this._commonImages.copy();
    const documentCombinationMedias = this._documentCombinationMedias.map((combinationMedia) =>
      combinationMedia.copy()
    );
    const brandkitCombinationMedias = this._brandkitPreviewMedias.map((combinationMedia) =>
      combinationMedia.copy()
    );
    const copy = new CarouselData(
      medias,
      combinationMedias,
      documentCombinationMedias,
      brandkitCombinationMedias
    );

    return copy;
  }

  static from(productPageMedias: HeroCarouselMedias): CarouselData {
    return new CarouselData(
      SlideArray.from(productPageMedias.common),
      productPageMedias.combinationMedias.map((combinationMedia) =>
        CombinationMedia.from(combinationMedia)
      ),
      productPageMedias.documentCombinationMedias?.map((combinationMedia) =>
        CombinationMedia.from(combinationMedia)
      )
    );
  }

  async addDocumentImage(
    currentDocument: ICurrentDocument,
    imagesTenant: string,
    merchant: string,
    accountId: string,
    locale: string,
    productKey: string,
    productVersion: number,
    selectedCombination: Combination,
    logger: ILogger,
    tokenConfig: AuthTokenHeader,
    client: FetchClient,
    signal?: AbortSignal
  ): Promise<boolean> {
    if (signal?.aborted) return false;

    const existingDocumentCombinationMedia = CarouselData.findStrictCompatibleCombinationMedia(
      this._documentCombinationMedias,
      selectedCombination
    );

    if (existingDocumentCombinationMedia) {
      return true;
    }

    const completedCoreCombination = await completeCombination(
      productKey,
      productVersion,
      merchant,
      accountId,
      locale,
      tokenConfig,
      client,
      logger,
      selectedCombination.combination,
      signal
    );

    if (signal?.aborted || !completedCoreCombination) return false;

    const completedCombination = new Combination(
      completedCoreCombination,
      selectedCombination.isInitial
    );
    const newDocumentCombinationMedia = CombinationMedia.fromClasses(
      completedCombination,
      SlideArray.empty()
    );

    this._documentCombinationMedias.push(newDocumentCombinationMedia);

    return await newDocumentCombinationMedia.createDocumentSlide(
      imagesTenant,
      locale,
      productKey,
      productVersion,
      currentDocument,
      logger
    );
  }

  async addBrandkitImage(
    locale: string,
    productKey: string,
    productVersion: number,
    designPersonalizationContextId: string,
    selectedCombination: Combination,
    altText: string,
    fetchClient: FetchClient,
    logger: ILogger
  ): Promise<boolean> {
    const existingBrandkitCombinationMedia = CarouselData.findCompatibleCombinationMedia(
      this._brandkitPreviewMedias,
      selectedCombination
    );

    if (existingBrandkitCombinationMedia) {
      return true;
    }

    const galleryContentQueryClient = new GalleryContentQueryClient(
      fetchClient,
      configuration.requestor
    );

    const matchingDesigns = await galleryContentQueryClient.getMatchingDesignsV0(
      locale,
      productKey,
      productVersion,
      designPersonalizationContextId,
      selectedCombination.combination,
      ScenesPurpose.DESIGN_REVIEW
    );

    if (matchingDesigns.isFailed) {
      logger.error('Failed to fetch brandkit matching designs', {
        productKey,
        productVersion,
        designPersonalizationContextId,
        endpoint: 'GalleryContentQueryClient.getMatchingDesignsV0',
        errorKind: 'brandkit',
      });
      return false;
    }

    const newCombination = new Combination(matchingDesigns.result.match.completeProductOptions);
    const previewUrl = matchingDesigns.result.match.colorVariants?.[0]?.previewUrl;
    if (!previewUrl) {
      logger.error('Brandkit matching design has no preview URL', {
        productKey,
        productVersion,
        designPersonalizationContextId,
        errorKind: 'brandkit',
      });
      return false;
    }
    const newCombinationMedia = CombinationMedia.fromClasses(
      newCombination,
      SlideArray.fromBrandkitPreview(previewUrl, altText)
    );

    this._brandkitPreviewMedias.push(newCombinationMedia);

    return true;
  }

  removeBrandkitImage(): boolean {
    const isAnyDocumentImageRemoved = this._brandkitPreviewMedias.length > 0;
    this._brandkitPreviewMedias = [];
    return isAnyDocumentImageRemoved;
  }

  removeDocumentImage(): boolean {
    const isAnyDocumentImageRemoved = this._documentCombinationMedias.length > 0;

    this._documentCombinationMedias = [];

    return isAnyDocumentImageRemoved;
  }

  private getCombinationMediasSlidesForCombination(selectedCombination: Combination): SlideArray {
    const selectedCombinationMedias = CarouselData.findStrictCompatibleCombinationMedia(
      this._combinationMedias,
      selectedCombination
    );

    const documentSelectedCombinationMedias = CarouselData.findStrictCompatibleCombinationMedia(
      this._documentCombinationMedias,
      selectedCombination
    );

    return SlideArray.fromSlides(
      documentSelectedCombinationMedias?.medias.all || [],
      selectedCombinationMedias?.medias.all || []
    );
  }

  private static findStrictCompatibleCombinationMedia(
    combinationMedias: CombinationMedia[],
    selectedCombination: Combination
  ): CombinationMedia | undefined {
    return combinationMedias.find(
      (c) =>
        (!selectedCombination.isEmpty && c.hasCompatibleCombination(selectedCombination)) ||
        (selectedCombination.isEmpty && c.combination.isEmpty)
    );
  }
  private static findCompatibleCombinationMedia(
    combinationMedias: CombinationMedia[],
    selectedCombination: Combination
  ): CombinationMedia | undefined {
    return combinationMedias.find((c) => selectedCombination.isCompatible(c.combination));
  }
}
